import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FigmaChevronDownIcon,
  CheckIcon,
  XCrossIcon
} from '../icons';
import BulletSparkIcon from '../icons/BulletSparkIcon';
import mapsCache from '../../utils/mapsCache';
import './VenueAmenities.css';

const WEEK_DAYS = [
  { key: 1, label: 'Monday' },
  { key: 2, label: 'Tuesday' },
  { key: 3, label: 'Wednesday' },
  { key: 4, label: 'Thursday' },
  { key: 5, label: 'Friday' },
  { key: 6, label: 'Saturday' },
  { key: 7, label: 'Sunday' }
];

const normalizeText = (value) => (value == null ? '' : String(value).trim());

const formatHourLabel = (value) => {
  const cleaned = normalizeText(value);
  if (!cleaned) return '--';
  if (cleaned.length >= 5) return cleaned.slice(0, 5);
  return cleaned;
};


export const isKasaVenue = (venue) => {
  const name = normalizeText(venue?.name || venue?.venue_name).toLowerCase();
  const slug = normalizeText(venue?.slug || venue?.venue_slug).toLowerCase();
  return slug === 'kasa-cafe' || name === 'kasa café' || name === 'kasa cafe';
};

const KasaOperatingHours = () => (
  <div className="kasa-hours-copy">
    <div className="kasa-hours-group">
      <p className="vd-acc-subtitle">Normal cafe opening hours:</p>
      <div className="venue-operating-hours-list">
        <div className="venue-operating-hours-row">
          <span>Monday – Thursday</span>
          <span>8:00am – 6:00pm</span>
        </div>
        <div className="venue-operating-hours-row">
          <span>Friday</span>
          <span>8:00am – 5:00pm</span>
        </div>
        <div className="venue-operating-hours-row">
          <span>Saturday</span>
          <span>11:00am – 5:00pm</span>
        </div>
        <div className="venue-operating-hours-row">
          <span>Sunday</span>
          <span>11:00am – 4:00pm</span>
        </div>
      </div>
    </div>
  </div>
);

const KasaBookingSub = ({ lead, detail }) => (
  <span className="kasa-booking-sub">
    <span>{lead}</span>
    <span className="kasa-booking-sub-dot" aria-hidden="true" />
    <span>{detail}</span>
  </span>
);

const KasaRateRow = ({ label, price, offer = false }) => (
  <div className="kasa-booking-rate">
    <span className="kasa-booking-rate-label">
      {label}
      {offer ? <span className="kasa-booking-offer-chip">Special offer</span> : null}
    </span>
    <span className="kasa-booking-price">{price}</span>
  </div>
);

export const KasaBookingOptions = () => (
  <div className="kasa-booking-options">
    <div className="kasa-booking-option">
      <div className="kasa-booking-option-copy">
        <span className="kasa-booking-title">Per person booking</span>
        <KasaBookingSub
          lead="Non-exclusive use of space"
          detail="During normal cafe opening hours"
        />
      </div>
      <span className="kasa-booking-price">£3/person per hour</span>
    </div>

    <div className="kasa-booking-option kasa-booking-option--rates">
      <div className="kasa-booking-option-copy">
        <span className="kasa-booking-title">Full venue hire</span>
        <KasaBookingSub
          lead="Exclusive use of space"
          detail="During and outside normal cafe opening hours"
        />
      </div>
      <div className="kasa-booking-rates">
        <KasaRateRow label="During normal cafe opening hours" price="£200/hr" />
        <KasaRateRow label="Outside normal cafe opening hours" price="£150/hr" />
        <KasaRateRow label="4 hours" price="£450" offer />
        <KasaRateRow label="9+ hours" price="£960" offer />
      </div>
    </div>
  </div>
);

const AmenitySection = ({ title, isExpanded, onToggle, children }) => {
  return (
    <>
      <div className="">
        <div
          className="vd-acc-head flex items-center justify-between cursor-pointer"
          onClick={onToggle}
        >
          <div className="flex items-center py-8">
            <span className="vd-acc-title">{title}</span>
          </div>
          <FigmaChevronDownIcon
            className={`chevron-icon ${isExpanded ? 'expanded' : ''}`}
            width="14"
            height="14"
          />
        </div>

        {isExpanded && (
          <div className="pb-4">
            {children}
          </div>
        )}
      </div>
      <div className="amenity-divider" />
    </>
  );
};

const AmenityItems = ({ title, items = [], list = [] }) => {
  return (
    <div className="flex flex-col gap-4 pb-8">
      {title && title.length > 0 && <div className="vd-acc-subtitle">
        <span>{title}</span>
      </div>}
      {items.length > 0 && <div className="vd-amenity-grid grid grid-cols-2 gap-8 gap-y-4">
        {items.map((item, index) => (
          <div key={index} className="vd-amenity-item">
            {item.available ? (
              <CheckIcon width="16" height="16" className="vd-amenity-tick" />
            ) : (
              <XCrossIcon width="16" height="16" className="vd-amenity-cross" />
            )}
            <span className={`vd-body-text ${!item.available ? 'line-through' : ''}`}>{item.name}</span>
          </div>
        ))}
      </div>}
      {list.length > 0 && <div className="vd-bullet-list">
        {list.map((item, index) => (
          <div key={index} className="vd-amenity-item">
            <BulletSparkIcon className="vd-bullet-icon" />
            <span className="vd-body-text">{String(item).replace(/^\s*[-–—•*]\s+/, '')}</span>
          </div>
        ))}
      </div>}
    </div>
  )
}

const VenueLocationMap = ({ venue }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    const initializeMap = async () => {
      if (!mapRef.current) return;

      try {
        // Wait for Google Maps API to be loaded
        await mapsCache.waitForGoogleMaps(10000);

        if (!window.google || !window.google.maps) {
          return;
        }

        // Get venue coordinates (use latitude/longitude if available, otherwise geocode the address)
        const center = venue.latitude && venue.longitude 
          ? { lat: parseFloat(venue.latitude), lng: parseFloat(venue.longitude) }
          : { lat: 51.5074, lng: -0.1278 }; // Default to London

        // Create map
        const map = new window.google.maps.Map(mapRef.current, {
          center: center,
          zoom: 15,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ],
          gestureHandling: 'cooperative',
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        mapInstanceRef.current = map;

        // Add marker
        const marker = new window.google.maps.Marker({
          position: center,
          map: map,
          title: venue.name || 'Venue Location',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#046242',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          }
        });

        markerRef.current = marker;

        // Add info window
        const infoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="padding: 8px; max-width: 200px;">
              <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: 600; color: #000;">${venue.name || 'Venue'}</h3>
              <p style="margin: 0; font-size: 12px; color: #374151;">${venue.address || venue.area || ''}</p>
            </div>
          `
        });

        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });

        // If we don't have coordinates, try to geocode the address
        if ((!venue.latitude || !venue.longitude) && (venue.address || venue.area)) {
          const geocoder = new window.google.maps.Geocoder();
          const address = venue.address || venue.area;
          
          geocoder.geocode({ address: address }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              map.setCenter(location);
              marker.setPosition(location);
            }
          });
        }

      } catch (error) {
      }
    };

    initializeMap();

    // Cleanup
    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      mapInstanceRef.current = null;
    };
  }, [venue]);

  return (
    <div 
      ref={mapRef} 
      className="w-full h-full"
      style={{ minHeight: '400px' }}
    />
  );
};

const VenueAmenities = ({
  venue,
  amenitiesData,
  operatingHours = {}
}) => {
  const [expandedSections, setExpandedSections] = useState({
    foodBeverage: true,
    eventRules: true,
    amenities: true,
    operatingHours: true,
    cancellationPolicy: true,
    location: true,
    parking: true
  });

  const operatingHoursRows = useMemo(
    () =>
      WEEK_DAYS.map((day) => {
        const dayHours =
          operatingHours?.[day.key] ||
          operatingHours?.[String(day.key)] ||
          null;
        const isOpen = Boolean(dayHours?.is_open);
        return {
          ...day,
          isOpen,
          openTime: formatHourLabel(dayHours?.open_time),
          closeTime: formatHourLabel(dayHours?.close_time)
        };
      }),
    [operatingHours]
  );

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="venue-amenities pb-12">
      {/* Food and Beverage Section */}
      <AmenitySection
        title="Food and beverage"
        isExpanded={expandedSections.foodBeverage}
        onToggle={() => toggleSection('foodBeverage')}
      >
        {/* In-house catering */}
        <AmenityItems
          title="In-house catering available"
          items={amenitiesData.foodBeverage.inHouseCatering}
        />

        {/* External catering */}
        <AmenityItems
          title="External catering allowed"
          items={amenitiesData.foodBeverage.externalCatering}
        />

        {/* Alcohol Section */}
        <AmenityItems
          title={amenitiesData.foodBeverage.alcohol.venueProvides ? 'Alcohol provided by the venue' : 'No alcohol provided by the venue'}
          items={[]}
        />
        {/* BYO Alcohol Section */}
        <AmenityItems
          title={
            amenitiesData.foodBeverage.alcohol.byoAllowed
              ? 'BYO alcohol allowed'
              : 'BYO alcohol not allowed'
          }
          items={
            amenitiesData.foodBeverage.alcohol.byoAllowed
              ? [
                  {
                    name: 'Corkage fee for BYO alcohol',
                    available: amenitiesData.foodBeverage.alcohol.corkageFee
                  }
                ]
              : []
          }
        />

      </AmenitySection>

      {/* Event Rules Section */}
      <AmenitySection
        title="Event rules"
        isExpanded={expandedSections.eventRules}
        onToggle={() => toggleSection('eventRules')}
      >
        {/* Allowed Events */}
        <AmenityItems
          title="Allowed events"
          items={amenitiesData.eventRules.allowedEvents}
        />
        {/* Host Rules */}
        <AmenityItems
          title="Host rules"
          list={amenitiesData.eventRules.hostRules}
        />
      </AmenitySection>

      {/* Amenities Section */}
      <AmenitySection
        title="Amenities"
        isExpanded={expandedSections.amenities}
        onToggle={() => toggleSection('amenities')}
      >
        {/* Amenities */}
        <AmenityItems
          items={amenitiesData.amenities}
        />
      </AmenitySection>

      {/* Operating Hours Section */}
      <AmenitySection
        title="Operating hours"
        isExpanded={expandedSections.operatingHours}
        onToggle={() => toggleSection('operatingHours')}
      >
        {isKasaVenue(venue) ? (
          <KasaOperatingHours />
        ) : (
          <div className="venue-operating-hours-list">
            {operatingHoursRows.map((row) => (
              <div key={row.key} className="venue-operating-hours-row">
                <span>{row.label}</span>
                <span>
                  {row.isOpen ? `${row.openTime} - ${row.closeTime}` : 'Closed'}
                </span>
              </div>
            ))}
          </div>
        )}
      </AmenitySection>

      {/* Cancellation policy — own section below Amenities */}
      <AmenitySection
        title="Cancellation policy"
        isExpanded={expandedSections.cancellationPolicy}
        onToggle={() => toggleSection('cancellationPolicy')}
      >
        <div className="flex flex-col gap-3 pb-4">
          <p className="vd-body-text">
            {amenitiesData.eventRules?.cancellationPolicy || 'Contact venue'}
          </p>
        </div>
      </AmenitySection>

      {/* Location Section */}
      <AmenitySection
        title="Location"
        isExpanded={expandedSections.location}
        onToggle={() => toggleSection('location')}
      >
        <div className="flex flex-col gap-4">
          <p className="vd-body-text">{venue.address || venue.area}</p>
          {/* Google Map */}
          {expandedSections.location && (
            <div className="vd-map w-full h-[400px] overflow-hidden">
              <VenueLocationMap venue={venue} />
            </div>
          )}
        </div>
      </AmenitySection>

      {/* Parking Section */}
      {amenitiesData.parking && amenitiesData.parking.length > 0 && <AmenitySection
        title="Parking"
        isExpanded={expandedSections.parking}
        onToggle={() => toggleSection('parking')}
      >
        <p>Parking information will be displayed here</p>
      </AmenitySection>}
    </div>
  );
};

export default VenueAmenities;

